import { Trans } from '@lingui/react/macro';
import '@warp-ds/components/icon';
import { Button } from '@warp-ds/react';
import { useState } from 'react';
import { triggerSurveyEvent } from '../../hooks/useHotjar.js';
import { getHuckConfig } from '../../utils/getHuckConfig.js';

interface AiDescriptionFeedbackProps {
  suggestionId: string | null;
  onFeedbackSubmitted?: () => void;
}

type FeedbackRating = 'THUMBS_UP' | 'THUMBS_DOWN';

export const AiDescriptionFeedback = ({ suggestionId, onFeedbackSubmitted }: AiDescriptionFeedbackProps) => {
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const config = getHuckConfig();

  const submitFeedback = async (rating: FeedbackRating) => {
    if (isSubmitting || feedbackSubmitted) return;

    setIsSubmitting(true);

    console.log('[AI Description Feedback] Submitting feedback:', { rating, itemId: config.itemId });

    try {
      // Submit feedback to backend
      const response = await fetch(`${config.root}/api/description/${config.itemId}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': config.csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({
          rating,
          ...(suggestionId && { id: suggestionId }),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      console.log('[AI Description Feedback] Feedback submitted successfully');

      // Trigger Hotjar survey (consent check is enforced inside triggerSurveyEvent)
      const surveyEventName = rating === 'THUMBS_UP' ? 'ai_description_positive' : 'ai_description_negative';
      console.log('[AI Description Feedback] Triggering Hotjar survey event:', surveyEventName);
      triggerSurveyEvent(surveyEventName);
      console.log('[AI Description Feedback] Hotjar survey event triggered');

      setFeedbackSubmitted(true);

      onFeedbackSubmitted?.();
    } catch (error) {
      // Silent failure - log but don't show error to user
      console.error('[AI Description Feedback] Failed to submit feedback:', error);
      // Still mark as submitted to prevent retry spam
      setFeedbackSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (feedbackSubmitted) {
    return (
      <div className="flex items-center gap-8 mt-8 justify-end min-h-32">
        <p className="s-text text-s">
          <Trans
            id="mobility-item-insertion.form.ai-description.feedback-thanks"
            comment="Thank you message after submitting feedback"
          >
            Thanks for your feedback!
          </Trans>
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-12 mt-8 justify-end" data-testid="ai-description-feedback">
      <div className="s-text text-s">
        <Trans
          id="mobility-item-insertion.form.ai-description.feedback-prompt"
          comment="Prompt asking user to rate the AI description suggestion"
        >
          How was the description suggestion?
        </Trans>
      </div>
      <div className="flex gap-8">
        <Button
          utility
          small
          onClick={() => submitFeedback('THUMBS_UP')}
          disabled={isSubmitting}
          aria-label="Thumbs up"
          data-testid="ai-description-feedback-like"
          className="flex items-center justify-center"
        >
          <w-icon name="Like" size="small" locale={config.locale} class="s-icon-primary" />
        </Button>
        <Button
          utility
          small
          onClick={() => submitFeedback('THUMBS_DOWN')}
          disabled={isSubmitting}
          aria-label="Thumbs down"
          data-testid="ai-description-feedback-dislike"
          className="flex items-center justify-center"
        >
          <w-icon name="Dislike" size="small" locale={config.locale} class="s-icon-primary" />
        </Button>
      </div>
    </div>
  );
};
